import React, { useState } from 'react';
interface Props {
  data: Record<string, string>;
  onChange: (data: Record<string, string>) => void;
}
interface PulseQuality {
  name: string;
  description: string;
  indications: string[];
}
interface PulsePosition {
  id: string;
  name: string;
  organ: string;
}
const pulseQualities: PulseQuality[] = [{
  name: 'Superficial',
  description: 'Felt with light pressure, disappears with heavy pressure',
  indications: ['External condition', 'Defensive Qi disorder']
}, {
  name: 'Deep',
  description: 'Only felt with heavy pressure',
  indications: ['Internal condition', 'Organ disorder']
}, {
  name: 'Slow',
  description: 'Less than 4 beats per breath',
  indications: ['Cold syndrome', 'Yang deficiency']
}, {
  name: 'Rapid',
  description: 'More than 5 beats per breath',
  indications: ['Heat syndrome']
}, {
  name: 'Empty',
  description: 'Soft and weak, disappears with pressure',
  indications: ['Qi and Blood deficiency']
}, {
  name: 'Full',
  description: 'Strong and forceful',
  indications: ['Excess condition']
}, {
  name: 'Slippery',
  description: 'Smooth and rolling like pearls',
  indications: ['Phlegm', 'Food retention', 'Pregnancy']
}, {
  name: 'Choppy',
  description: 'Uneven and rough',
  indications: ['Blood stasis', 'Qi stagnation']
}, {
  name: 'Wiry',
  description: 'Tense like a guitar string',
  indications: ['Liver disorder', 'Pain', 'Stress']
}];
const pulsePositions: PulsePosition[] = [{
  id: 'left-cun',
  name: 'Left Cun (Inch)',
  organ: 'Heart/Small Intestine'
}, {
  id: 'left-guan',
  name: 'Left Guan (Bar)',
  organ: 'Liver/Gallbladder'
}, {
  id: 'left-chi',
  name: 'Left Chi (Cubit)',
  organ: 'Kidney Yin'
}, {
  id: 'right-cun',
  name: 'Right Cun (Inch)',
  organ: 'Lung/Large Intestine'
}, {
  id: 'right-guan',
  name: 'Right Guan (Bar)',
  organ: 'Spleen/Stomach'
}, {
  id: 'right-chi',
  name: 'Right Chi (Cubit)',
  organ: 'Kidney Yang'
}];
export function PulseDiagnosis({
  data,
  onChange
}: Props) {
  const [hoveredPosition, setHoveredPosition] = useState<string | null>(null);
  const handleChange = (position: string, quality: string) => {
    onChange({
      ...data,
      [position]: quality
    });
  };
  const getQualityDetails = (qualityName: string) => {
    return pulseQualities.find(q => q.name === qualityName);
  };
  return <div className="space-y-6" data-id="element-15">
      <div data-id="element-16">
        <h2 className="text-2xl font-bold text-gray-800 mb-4" data-id="element-17">
          💓 Pulse Diagnosis
        </h2>
        <p className="text-gray-600 mb-6" data-id="element-18">
          Select the pulse quality for each position. The pulse diagnosis
          provides valuable insights into the patient's overall condition.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-19">
        {/* Pulse positions */}
        <div data-id="element-20">
          <h3 className="text-lg font-semibold mb-4" data-id="element-21">Pulse Positions</h3>
          <div className="bg-blue-50 rounded-lg p-6 space-y-4" data-id="element-22">
            {pulsePositions.map(position => <PulsePositionCard key={position.id} position={position} selectedQuality={data[position.id]} qualityDetails={data[position.id] ? getQualityDetails(data[position.id]) : null} isHovered={hoveredPosition === position.id} onHoverChange={isHovered => setHoveredPosition(isHovered ? position.id : null)} onChange={quality => handleChange(position.id, quality)} availableQualities={pulseQualities} data-id="element-23" />)}
          </div>
        </div>
        {/* Pulse qualities reference */}
        <div data-id="element-24">
          <h3 className="text-lg font-semibold mb-4" data-id="element-25">
            Pulse Qualities Reference
          </h3>
          <div className="bg-gray-50 rounded-lg p-4 max-h-[500px] overflow-y-auto" data-id="element-26">
            {pulseQualities.map(quality => <PulseQualityReference key={quality.name} quality={quality} data-id="element-27" />)}
          </div>
        </div>
      </div>
    </div>;
}
function PulsePositionCard({
  position,
  selectedQuality,
  qualityDetails,
  isHovered,
  onHoverChange,
  onChange,
  availableQualities
}: {
  position: PulsePosition;
  selectedQuality: string | undefined;
  qualityDetails: PulseQuality | null | undefined;
  isHovered: boolean;
  onHoverChange: (isHovered: boolean) => void;
  onChange: (quality: string) => void;
  availableQualities: PulseQuality[];
}) {
  return <div className="bg-white p-4 rounded-lg shadow-sm relative" data-id="element-28">
      <div className="flex justify-between items-center mb-2" data-id="element-29">
        <h4 className="font-medium" data-id="element-30">{position.name}</h4>
        <span className="text-sm text-blue-600" data-id="element-31">{position.organ}</span>
      </div>
      <select className="w-full p-2 border border-gray-300 rounded" value={selectedQuality || ''} onChange={e => onChange(e.target.value)} data-id="element-32">
        <option value="" data-id="element-33">Select pulse quality</option>
        {availableQualities.map(quality => <option key={quality.name} value={quality.name} data-id="element-34">
            {quality.name}
          </option>)}
      </select>
      {/* Selected quality with hover tooltip */}
      {selectedQuality && qualityDetails && <div className="mt-3 relative" onMouseEnter={() => onHoverChange(true)} onMouseLeave={() => onHoverChange(false)} data-id="element-35">
          <div className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium cursor-help" data-id="element-36">
            {selectedQuality}
          </div>
          {/* Tooltip */}
          {isHovered && <div className="absolute z-10 left-0 mt-2 w-full bg-gray-900 text-white p-4 rounded-lg shadow-xl" data-id="element-37">
              <h5 className="font-bold text-lg mb-2" data-id="element-38">{qualityDetails.name}</h5>
              <p className="text-sm mb-2" data-id="element-39">{qualityDetails.description}</p>
              <div className="text-sm" data-id="element-40">
                <span className="font-semibold" data-id="element-41">Indicates: </span>
                {qualityDetails.indications.join(', ')}
              </div>
              {/* Arrow pointer */}
              <div className="absolute -top-2 left-4 w-4 h-4 bg-gray-900 transform rotate-45" data-id="element-42"></div>
            </div>}
        </div>}
    </div>;
}
function PulseQualityReference({
  quality
}: {
  quality: PulseQuality;
}) {
  return <div className="mb-4 p-3 bg-white rounded shadow-sm" data-id="element-43">
      <h4 className="font-medium text-green-700" data-id="element-44">{quality.name}</h4>
      <p className="text-sm text-gray-600 mt-1" data-id="element-45">{quality.description}</p>
      <div className="mt-2" data-id="element-46">
        <span className="text-xs font-medium text-gray-500" data-id="element-47">Indicates: </span>
        <span className="text-xs" data-id="element-48">{quality.indications.join(', ')}</span>
      </div>
    </div>;
}